/*
 * Copyright (c) Nexign, JSC, 2021-2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.21.0
 * @since 1.13.0
 */

import { ChangeEvent, Fragment, useEffect, useState } from 'react';

import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';

import { EFormatData } from '@/main/pages/PageDashboard/types/widgetTypes';
import { useSubscriberIdContext } from '@/main/pages/PageSubscriberId/context';
import { ESubscriberIdActions as Action } from '@/main/pages/PageSubscriberId/types';
import { formatDate } from '@/main/utils/formatText';
import { stringFormatParams } from '@/main/utils/stringFormat';
import { icons } from '@assets/index';
import { Button, Input, LinkBlock, Skeleton } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import EllipsisText from '@components/common/EllipsisText/EllipsisText';
import { ELinkTarget } from '@components/common/ExternalLink/ExternalLink';
import { EInputKind } from '@components/common/Input/Input';
import { TooltipModalChildStyled } from '@components/common/Tooltip/styled/TooltipModalStyled';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import {
    API_SUBSCRIBER_ID_FILE_PARAM,
    API_SUBSCRIBER_ID_PARAM,
    API_SUBSCRIBER_ID_PERSONAL_EDIT_FILE_DOWNLOAD,
} from '@modules/app/Api';
import { useModalOpenError } from '@modules/modal/hooks/useModalOpenError';
import { useModalOpenSuccess } from '@modules/modal/hooks/useModalOpenSuccess';
import { modalOpen } from '@modules/modal/modalActions';
import { EModalStatus, IModalRenderProps } from '@modules/modal/types/modalTypes';
import ServiceUserDenied from '@modules/role/components/ServiceUserDenied';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';

import { useCheckCustomRole } from '../../../../../utils/useCheckCustomRole';
import SubscriberIdError from '../../SubscriberIdError';
import { personalDataTranslationSelector } from '../selectors';
import {
    useConfirmPersonalDataMutation,
    useGetPersonalDataFileQuery,
    useGetPersonalDataInfoQuery,
    useGetPersonalDataMetaQuery,
    useUpdatePersonalDataEmailMutation,
} from '../store';
import { IPersonalDataFile } from '../types/file';
import {
    SubscriberInfoDataEmailFieldStyled,
    SubscriberInfoDataInfoStyled,
} from './styled/SubscriberInfoDataInfoStyled';
import SubscriberIdDeleteModal from './SubscriberIdDeleteModal';
import { SubscriberIdPersonalDataInfoSkeleton } from './SubscriberIdPersonalDataSkeleton';


interface ISubscriberPersonalDataInfo {
    onEdit: () => void;
}

const SubscriberPersonalDataInfo = ({ onEdit }: ISubscriberPersonalDataInfo) => {

    const { subscriberId, userType } = useSubscriberIdContext();

    const dispatch = useDispatch();

    const tabTranslation = useSelector(personalDataTranslationSelector());

    const { open: openModalSuccess } = useModalOpenSuccess();
    const { open: openModalError } = useModalOpenError();

    const { actions, defaultDoc } = useGetPersonalDataMetaQuery(subscriberId, {
        selectFromResult: ({ data }) => ({
            actions: data?.actions ?? {},
            defaultDoc: data?.docTypes[0],
        }),
    });

    const { data, isFetching, error, refetch } = useGetPersonalDataInfoQuery({ subscriberId, defaultDoc });

    const {
        data: file,
        isFetching: isFileLoading,
        error: isFileError,
        refetch: fileRefetch,
    } = useGetPersonalDataFileQuery(subscriberId, {
        skip: !data || data?.isEmpty,
    });

    const [confirm, { isLoading: isConfirmLoading }] = useConfirmPersonalDataMutation();

    const [updateEmail, { isLoading: isEmailLoading }] = useUpdatePersonalDataEmailMutation();

    const isCustomRoleCheckAvailable = useCheckCustomRole(true, AuthorityRoleEnum.ROLE_PERSONAL_MANAGE);

    // Исключение в использовании useCheckCustomRole
    // Для случаев когда пользователь B2G хочет посмотреть перс. данные по своему абоненту
    const showPersonalData = isCustomRoleCheckAvailable || data?.isUserOwnerOfPersonalData;

    const [isEmailEdit, saveEmailEdit] = useState(false);
    const [isEmailValid, saveEmailValid] = useState(false);
    const [emailValue, saveEmailValue] = useState(data?.personalData.email);

    useEffect(() => {
        saveEmailEdit(false);
        saveEmailValue(data?.personalData.email);
    }, [data]);

    useEffect(() => {
        Yup.string().email().nullable(true).isValid(emailValue).then(value => {
            saveEmailValid(value);
        });
    }, [emailValue]);

    const onConfirm = () => {
        confirm(subscriberId)
            .then(() => {
                openModalSuccess(tabTranslation.personalDataEdit.modal[EModalStatus.success].confirm);
            })
            .catch(() => {
                openModalError(tabTranslation.personalDataEdit.modal[EModalStatus.failure].confirm);
            });
    };

    const onChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
        saveEmailValue(event.target.value);
    };

    const onUpdateEmail = () => {
        if (isEmailValid && emailValue !== data?.personalData.email) {
            updateEmail({ subscriberId, email: emailValue || '' });
        }
    };

    const onDelete = () => {
        dispatch(modalOpen({
            render: (props: IModalRenderProps) => <SubscriberIdDeleteModal
                subscriberId={subscriberId}
                userType={userType}
                onCloseModal={props.onCloseModal}
            />,
        }));
    };

    const renderBlock = (label?: string, value?: string) => value && (
        <div className="personal-data__group">
            <div className="subscriber-id__title subscriber-id__title_light">{label || ''}</div>
            <EllipsisText lines={1} title={value}>{value}</EllipsisText>
        </div>
    );

    const getFileDownloadUrl = (file: IPersonalDataFile) =>
        stringFormatParams(API_SUBSCRIBER_ID_PERSONAL_EDIT_FILE_DOWNLOAD, {
            [API_SUBSCRIBER_ID_PARAM]: subscriberId,
            [API_SUBSCRIBER_ID_FILE_PARAM]: file.id,
        });

    const getFormattedDate = (date: string | undefined) => date
        ? formatDate(date, { format: EFormatData.FullData, skipZone: true }) : '';


    const getTooltipFromConflict = (action: Action): string =>
        actions[action]?.conflicts
            ?.map(conflict => tabTranslation.tooltip[conflict.code] || conflict.message).join('\n\n')
        ?? '';

    if (error) return <div style={{ marginTop: 32 }}><SubscriberIdError refetch={refetch}/></div>;
    if (isFetching) return <Skeleton isLoading render={SubscriberIdPersonalDataInfoSkeleton}/>;
    if (!data) return null;

    const { isEmpty, document, personalData, address } = data;
    const { document: tDocument, personalData: tPersonalData } = tabTranslation;

    return (
        <SubscriberInfoDataInfoStyled>{
            !isEmpty && (
                <Fragment>
                    <div className="personal-data__block">
                        <div className="subscriber-id__title subscriber-id__title_bold personal-data__title">
                            {tPersonalData.title}
                        </div>
                        {
                            renderBlock(tPersonalData.fields.fullName, personalData.fullName)
                        }
                        {
                            renderBlock(tPersonalData.fields.birthDate, getFormattedDate(personalData.birthDate))
                        }
                        {
                            renderBlock(tPersonalData.fields.nationalityInfo,
                                personalData.nationalityInfo?.addressString)
                        }
                        {
                            renderBlock(tPersonalData.fields.birthPlace, personalData.birthPlace)
                        }
                        {showPersonalData && (
                            <div className="personal-data__group personal-data__group_email">
                                <div className="subscriber-id__title subscriber-id__title_light">
                                    {
                                        tPersonalData.fields.email
                                    }
                                    {
                                        !isEmailEdit && <ServiceUserDenied renderComponent={disabled => (
                                            <ImgStyled
                                                className={classNames('medium', { 'pointer': !disabled })}
                                                src={disabled ? icons.editDisabledIcon : icons.editGreenIcon}
                                                onClick={() => !disabled && saveEmailEdit(true)}
                                            />)}
                                        />
                                    }
                                </div>
                                {
                                    isEmailEdit
                                        ? <SubscriberInfoDataEmailFieldStyled>
                                            <Input
                                                kind={isEmailValid ? EInputKind.normal : EInputKind.error}
                                                righticon={isEmailLoading ? icons.searchIndicator : ''}
                                                value={emailValue}
                                                onChange={onChangeEmail}
                                            />
                                            {
                                                !isEmailLoading && (
                                                    <Fragment>
                                                        <ImgStyled
                                                            className="medium pointer"
                                                            src={(isEmailValid && emailValue !== personalData.email)
                                                                ? icons.successIcon : icons.disabledIcon}
                                                            onClick={onUpdateEmail}
                                                        />
                                                        <ImgStyled
                                                            className="medium pointer"
                                                            src={icons.cancelCircleGreenIcon}
                                                            onClick={() => saveEmailEdit(false)}
                                                        />
                                                    </Fragment>
                                                )
                                            }
                                        </SubscriberInfoDataEmailFieldStyled>
                                        : <SubscriberInfoDataEmailFieldStyled>
                                            <EllipsisText lines={1} title={personalData.email}>
                                                {personalData.email}
                                            </EllipsisText>

                                        </SubscriberInfoDataEmailFieldStyled>
                                }
                        </div>)}

                    </div>
                    {showPersonalData && (
                        <div className="personal-data__block">
                            <div className="subscriber-id__title subscriber-id__title_bold personal-data__title">
                                {tDocument.title}
                            </div>
                            {
                                renderBlock(tDocument.fields.type, document.type.label)
                            }
                            {
                                renderBlock(tDocument.fields.series, document.series)
                            }
                            {
                                renderBlock(tDocument.fields.number, document.number)
                            }
                            {
                                renderBlock(tDocument.fields.dateOfIssue, getFormattedDate(document.dateOfIssue))
                            }
                            {
                                renderBlock(tDocument.fields.validFor, getFormattedDate(document.validFor))
                            }
                            {
                                renderBlock(tDocument.fields.providedByOrganization, document.providedByOrganization)
                            }
                            {
                                renderBlock(tDocument.fields.divisionCode, document.divisionCode)
                            }
                            <div className="personal-data__block-file">
                                {isFileError
                                    ? <SubscriberIdError left withoutImage refetch={fileRefetch}/>
                                    : <div className="personal-data__group">
                                        <div className="subscriber-id__title subscriber-id__title_light">
                                            {tabTranslation.files.fields.name}
                                        </div>
                                        {
                                            isFileLoading && <ImgStyled
                                                className="medium"
                                                src={icons.searchIndicator}
                                                style={{ width: 32 }}
                                            />
                                        }
                                        {
                                            file?.map(file => (
                                                <div key={file.id}>
                                                    <LinkBlock isExternalUrl target={ELinkTarget.BLANK}
                                                               url={getFileDownloadUrl(file)}>
                                                        {file.name}
                                                    </LinkBlock>
                                                </div>
                                            ))
                                        }
                                    </div>
                                }
                            </div>
                        </div>)}
                    {
                        address && showPersonalData && (
                            <div className="personal-data__block personal-data__block_address">
                                <div className="subscriber-id__title subscriber-id__title_bold personal-data__title">
                                    {tabTranslation.address.title}
                                </div>
                                {address?.addressString}
                            </div>
                        )
                    }
                </Fragment>
            )}

            {showPersonalData && (
                <div className="personal-data__btn-container">
                    {
                        actions[Action.personalDataEdit]?.isVisible &&
                        <span className="personal-data__btn">
                            <ArrowTooltip
                                placement="top"
                                title={tabTranslation.tooltip[actions[Action.personalDataEdit]?.reason ?? '']}
                                titleAlign="center"
                            >
                                <TooltipModalChildStyled>
                                    <Button
                                        disabled={actions[Action.personalDataEdit]?.isDisabled || isFileLoading}
                                        onClick={onEdit}
                                    >
                                        {isEmpty ? tabTranslation.btn.add : tabTranslation.btn.edit}
                                    </Button>
                                </TooltipModalChildStyled>
                            </ArrowTooltip>
                        </span>
                    }
                    {
                        actions[Action.personalDataConfirm]?.isVisible &&
                        <Button
                            className="personal-data__btn"
                            disabled={actions[Action.personalDataConfirm]?.isDisabled || isConfirmLoading}
                            onClick={onConfirm}
                        >
                            {tabTranslation.btn.confirm}
                        </Button>
                    }
                    {
                        actions[Action.personalDataDelete]?.isVisible &&
                        <span className="personal-data__btn">
                            <ArrowTooltip
                                placement="top"
                                title={getTooltipFromConflict(Action.personalDataDelete)}
                                titleAlign="center"
                            >
                                <TooltipModalChildStyled>
                                    <LinkBlock
                                        dataName="personal-data-delete-btn"
                                        disabled={actions[Action.personalDataDelete]?.isDisabled || isConfirmLoading}
                                        onClick={onDelete}
                                    >
                                        {tabTranslation.btn.delete}
                                    </LinkBlock>
                                </TooltipModalChildStyled>
                            </ArrowTooltip>
                        </span>
                    }
                </div>)}
        </SubscriberInfoDataInfoStyled>
    );
};

export default SubscriberPersonalDataInfo;
